import { Settings2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type ColumnOption<K extends string> = { key: K; label: string };

// A compact "Customize columns" control: a gear button that opens a popover
// of column toggles. Replaces the former right-rail Columns panel so the
// trades table can use the full width. State is owned by the caller; this
// component is purely the trigger + popover surface.
export function ColumnsButton<K extends string>({
  columns,
  visibility,
  onToggle,
}: {
  columns: readonly ColumnOption<K>[];
  visibility: Record<K, boolean>;
  onToggle: (key: K, checked: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Customize columns"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex h-9 items-center gap-1.5 rounded-control border border-outline-variant px-3 text-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
      >
        <Settings2 aria-hidden="true" className="h-4 w-4" />
        Columns
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Customize columns"
          className="absolute right-0 z-20 mt-2 w-56 rounded-control border border-outline-variant bg-surface-container p-3 shadow-lg"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
            Customize columns
          </p>
          <ul className="space-y-1.5">
            {columns.map((col) => (
              <li key={col.key}>
                <label className="flex items-center gap-2 text-sm text-on-surface">
                  <input
                    type="checkbox"
                    checked={visibility[col.key]}
                    onChange={(event) => onToggle(col.key, event.target.checked)}
                    className="h-4 w-4"
                  />
                  {col.label}
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
